import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const CharacterDetectiveGame = ({ gameData, onComplete }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [gamePhase, setGamePhase] = useState('playing'); // playing, complete

    const currentCharacter = gameData?.characters?.[currentIndex];

    const handleCharacterSelect = (character) => {
        if (selectedCharacter || feedback) return;
        triggerHaptic(30);
        setSelectedCharacter(character);

        if (character.id === currentCharacter.id) {
            setFeedback('correct');
            const pointsToAdd = gameData.points_per_match || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                setSelectedCharacter(null);

                if (currentIndex < gameData.characters.length - 1) {
                    setCurrentIndex(prev => prev + 1);
                } else {
                    setGamePhase('complete');
                    triggerHaptic([200, 100, 200, 100, 300]);
                    setTimeout(() => onComplete?.(newScore), 4000);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setSelectedCharacter(null);
            }, 2000);
        }
    };

    if (!gameData || !gameData.characters) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-fuchsia-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🕵️‍♂️</div>
                <h3 className="text-base md:text-3xl font-black text-fuchsia-900 tracking-tighter uppercase">Ruošiamas dosjė...</h3>
            </div>
        );
    }

    const progress = (currentIndex / gameData.characters.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-fuchsia-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-fuchsia-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-fuchsia-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🕵️‍♂️
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Veikėjų Detektyvas
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-fuchsia-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Byla</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentIndex + 1}/{gameData.characters.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-fuchsia-400 via-purple-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(192,38,211,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none">📜</div>
                <h4 className="text-[10px] font-black text-fuchsia-400 uppercase tracking-[0.5em] mb-4 md:mb-10 text-center">Slapta Užuomina</h4>
                
                <div className="relative group w-full max-w-lg">
                    <div className="absolute -inset-10 bg-fuchsia-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                    <div className="bg-white px-6 py-8 md:px-10 md:py-12 rounded-[2rem] md:rounded-[3.5rem] shadow-2xl border-4 border-fuchsia-100 relative z-10">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 md:w-20 md:h-20 bg-fuchsia-600 text-white rounded-full flex items-center justify-center text-xl md:text-4xl shadow-2xl border-4 md:border-8 border-white animate-bounce">🕵️‍♀️</div>
                        <p className="text-base md:text-3xl font-black text-slate-800 leading-tight tracking-tighter italic font-serif">
                            "{currentCharacter?.description}"
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto">
            <div className="flex items-center gap-2 px-4 self-start">
                <div className="w-2 h-2 bg-fuchsia-500 rounded-full animate-pulse"></div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Įtariamųjų Sąrašas</h4>
            </div>
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden w-full min-h-[400px]">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">📡</div>
                <div className="grid grid-cols-2 gap-2 md:gap-6 relative z-10">
                    {gameData.characters.map((char) => {
                        const isSelected = selectedCharacter?.id === char.id;
                        const isCorrectInFeed = feedback === 'correct' && isSelected;
                        const isWrongInFeed = feedback === 'wrong' && isSelected;

                        return (
                            <button
                                key={char.id}
                                onClick={() => handleCharacterSelect(char)}
                                disabled={!!feedback}
                                className={`
                                    premium-button-3d relative rounded-xl md:rounded-[3rem] p-4 md:p-8 flex flex-col items-center justify-center text-center transition-all duration-500 transform border-b-8 md:border-b-[12px]
                                    ${isCorrectInFeed
                                        ? 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-[0_0_50px_rgba(16,185,129,0.5)]'
                                        : isWrongInFeed
                                            ? 'bg-rose-500 text-white border-rose-700 animate-shake'
                                            : isSelected
                                                ? 'bg-fuchsia-600 text-white border-fuchsia-800'
                                                : 'bg-slate-800/80 border-slate-700 text-white hover:bg-slate-800 active:translate-y-1 active:border-b-0'}
                                    disabled:cursor-default
                                `}
                            >
                                <div className={`text-3xl md:text-7xl mb-2 md:mb-4 drop-shadow-2xl transition-transform ${isSelected ? 'animate-bounce' : ''}`}>
                                    {char.emoji}
                                </div>
                                <h4 className={`text-xs md:text-2xl font-black tracking-tighter uppercase leading-none ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                                    {char.name}
                                </h4>
                                {isCorrectInFeed && (
                                    <div className="absolute -top-2 -right-2 w-8 h-8 md:w-12 md:h-12 bg-white text-emerald-600 rounded-full flex items-center justify-center text-sm md:text-2xl shadow-xl border-2 md:border-4 border-emerald-100 animate-pop-in">✓</div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🎯' : '🔦'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'ATPAŽINTA!' : 'KLAIDA!'}
                            </h2>

                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center">
                                    {feedback === 'correct' ? `Teisingai! Tai tikrai ${currentCharacter?.name}.` : 'Šis veikėjas neatitinka aprašymo.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {gamePhase === 'complete' && (
                <div className="fixed inset-0 flex items-center justify-center z-[3000] p-4">
                    <div className="absolute inset-0 bg-fuchsia-900/90 backdrop-blur-2xl animate-fade-in" />
                    <div className="max-w-2xl w-full p-8 md:p-16 text-center animate-pop-in flex flex-col items-center">
                        <div className="text-6xl md:text-[150px] mb-8 drop-shadow-2xl animate-float">🥇🕵️‍♂️</div>
                        <h3 className="text-4xl md:text-7xl font-black text-white tracking-tighter uppercase leading-none mb-8">Byla Išspręsta!</h3>
                        <div className="bg-white/10 rounded-[2rem] md:rounded-[4rem] p-8 md:p-14 text-white shadow-2xl border-4 border-white/20 w-full">
                            <div className="flex items-center justify-center gap-4 md:gap-12">
                                <div className="text-center">
                                    <p className="text-4xl md:text-8xl font-black italic text-fuchsia-400">{gameData.characters.length}</p>
                                    <p className="text-[10px] md:text-sm font-black uppercase tracking-widest text-fuchsia-300">Veikėjai</p>
                                </div>
                                <div className="w-1 h-16 md:h-24 bg-white/10" />
                                <div className="text-center">
                                    <p className="text-4xl md:text-8xl font-black italic text-emerald-400">{score}</p>
                                    <p className="text-[10px] md:text-sm font-black uppercase tracking-widest text-emerald-300">Taškai</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CharacterDetectiveGame;
